import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { 
  ArrowLeft, Send, Wallet, Copy, ExternalLink, CheckCircle,
  AlertTriangle, History, ArrowUpRight, RefreshCw, Car, CreditCard,
  Building, DollarSign, FileText, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ethers } from 'ethers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BETH_TOKEN = "0x6822824c5349bf005e353ec5dda3fa34595ea811";

// Simple ERC20 ABI for transfer
const ERC20_ABI = [
  "function transfer(address to, uint256 amount) returns (bool)",
  "function balanceOf(address owner) view returns (uint256)",
  "function decimals() view returns (uint8)"
];

// Payment provider wallet addresses (example addresses - replace with actual)
const PAYMENT_PROVIDERS = {
  bitpay: {
    name: 'BitPay',
    description: 'Pay auto loans with crypto via BitPay',
    wallet: '0x1234567890123456789012345678901234567890', // Replace with actual BitPay merchant address
    logo: '💳',
    color: 'blue',
    website: 'https://bitpay.com',
    features: ['Instant conversion to USD', 'Low fees (1%)', 'Bank settlement next day']
  },
  wctpay: {
    name: 'WCT Pay',
    description: 'Auto financing payments with crypto',
    wallet: '0x0987654321098765432109876543210987654321', // Replace with actual WCT Pay address
    logo: '🚗',
    color: 'green',
    website: 'https://wctpay.com',
    features: ['Direct lender payments', 'Auto loan specialized', 'Payment confirmation']
  }
};

const TransferBeth = () => {
  const { token } = useAuth();
  const { account, bethBalance, refreshBalances } = useWeb3();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('transfer'); // 'transfer' or 'autoloan'
  const [recipientAddress, setRecipientAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [sending, setSending] = useState(false);
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showSaveAddress, setShowSaveAddress] = useState(false);
  const [newAddressLabel, setNewAddressLabel] = useState('');
  
  // Auto loan payment state
  const [selectedProvider, setSelectedProvider] = useState('bitpay');
  const [loanId, setLoanId] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');

  useEffect(() => {
    loadSavedAddresses();
    loadRecentTransfers();
  }, []);

  const loadSavedAddresses = () => {
    const saved = localStorage.getItem('bethSavedAddresses');
    if (saved) {
      setSavedAddresses(JSON.parse(saved));
    }
  };

  const loadRecentTransfers = async () => {
    try {
      const response = await fetch(`${API}/user/transfers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRecentTransfers(data.transfers || []);
      }
    } catch (error) {
      console.error('Error loading transfers:', error);
    }
  };

  const saveAddress = () => {
    if (!recipientAddress || !newAddressLabel) {
      toast.error('Please enter both address and label');
      return;
    }
    
    const newAddress = {
      address: recipientAddress.toLowerCase(),
      label: newAddressLabel,
      savedAt: new Date().toISOString()
    };
    
    const updated = [...savedAddresses, newAddress];
    setSavedAddresses(updated);
    localStorage.setItem('bethSavedAddresses', JSON.stringify(updated));
    setShowSaveAddress(false);
    setNewAddressLabel('');
    toast.success('Address saved!');
  };

  const removeSavedAddress = (address) => {
    const updated = savedAddresses.filter(a => a.address !== address);
    setSavedAddresses(updated);
    localStorage.setItem('bethSavedAddresses', JSON.stringify(updated));
    toast.success('Address removed');
  };

  const selectSavedAddress = (address) => {
    setRecipientAddress(address);
  };

  const validateAddress = (address) => {
    try {
      return ethers.utils.isAddress(address);
    } catch {
      return false;
    }
  };

  const handleTransfer = async () => {
    if (!account) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!recipientAddress || !validateAddress(recipientAddress)) {
      toast.error('Please enter a valid Ethereum address');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (parseFloat(amount) > bethBalance) {
      toast.error('Insufficient BETH balance');
      return;
    }

    setSending(true);
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const bethContract = new ethers.Contract(BETH_TOKEN, ERC20_ABI, signer);
      const amountWei = ethers.utils.parseUnits(amount, 18);
      
      toast.info('Please confirm the transaction in MetaMask...');
      const tx = await bethContract.transfer(recipientAddress, amountWei);
      
      toast.info('Transaction submitted. Waiting for confirmation...');
      await tx.wait();
      
      // Record transfer
      await fetch(`${API}/user/transfers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          to: recipientAddress,
          amount: parseFloat(amount),
          tx_hash: tx.hash
        })
      });

      // Send email notification
      fetch(`${API}/notifications/send-transfer-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          to_address: recipientAddress,
          amount: parseFloat(amount),
          tx_hash: tx.hash
        })
      }).catch(() => {});

      toast.success('Transfer successful!');
      setAmount('');
      setRecipientAddress('');
      refreshBalances();
      loadRecentTransfers();
      
    } catch (error) {
      console.error('Transfer error:', error);
      if (error.code === 'ACTION_REJECTED') {
        toast.error('Transaction cancelled');
      } else {
        toast.error(error.message || 'Transfer failed');
      }
    } finally {
      setSending(false);
    }
  };

  const handleAutoLoanPayment = async () => {
    if (!account) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    if (parseFloat(paymentAmount) > bethBalance) {
      toast.error('Insufficient BETH balance');
      return;
    }

    const provider = PAYMENT_PROVIDERS[selectedProvider];
    if (!provider) {
      toast.error('Please select a payment provider');
      return;
    }

    setSending(true);
    try {
      const ethersProvider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = ethersProvider.getSigner();
      const bethContract = new ethers.Contract(BETH_TOKEN, ERC20_ABI, signer);
      const amountWei = ethers.utils.parseUnits(paymentAmount, 18);
      
      toast.info(`Initiating ${provider.name} payment. Please confirm in MetaMask...`);
      const tx = await bethContract.transfer(provider.wallet, amountWei);
      
      toast.info('Transaction submitted. Waiting for confirmation...');
      await tx.wait();
      
      // Record auto loan payment
      await fetch(`${API}/auto-loan-payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          provider: selectedProvider,
          loan_id: loanId || 'N/A',
          amount: parseFloat(paymentAmount),
          tx_hash: tx.hash,
          note: paymentNote
        })
      });

      toast.success(`Auto loan payment via ${provider.name} successful!`);
      setPaymentAmount('');
      setLoanId('');
      setPaymentNote('');
      refreshBalances();
      loadRecentTransfers();
      
    } catch (error) {
      console.error('Auto loan payment error:', error);
      if (error.code === 'ACTION_REJECTED') {
        toast.error('Transaction cancelled');
      } else {
        toast.error(error.message || 'Payment failed');
      }
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="transfer-beth">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Send className="w-5 h-5 text-[#b8860b]" />
                Transfer & Pay
              </h1>
              <p className="text-xs text-gray-500">Send BETH or pay auto loans with crypto</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 p-1 bg-[#0d1d35] rounded-lg border border-[#1a2d4a]">
          <button
            onClick={() => setActiveTab('transfer')}
            className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all ${
              activeTab === 'transfer'
                ? 'bg-[#b8860b] text-white'
                : 'text-gray-400 hover:text-white hover:bg-[#1a2d4a]'
            }`}
          >
            <Send className="w-4 h-4" />
            Transfer BETH
          </button>
          <button
            onClick={() => setActiveTab('autoloan')}
            className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all ${
              activeTab === 'autoloan'
                ? 'bg-green-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-[#1a2d4a]'
            }`}
          >
            <Car className="w-4 h-4" />
            Pay Auto Loan
          </button>
        </div>

        {/* Wallet Connection Check */}
        {!account ? (
          <Card className="bg-yellow-900/20 border-yellow-500/30">
            <CardContent className="py-6 text-center">
              <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <p className="text-white font-medium mb-2">Wallet Not Connected</p>
              <p className="text-gray-400 text-sm mb-4">Connect your MetaMask wallet to transfer BETH or pay loans</p>
              <Button onClick={() => navigate('/dashboard')} className="bg-[#b8860b] hover:bg-[#9a7209]">
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Balance Card */}
            <Card className="bg-gradient-to-br from-[#b8860b]/20 to-[#b8860b]/5 border-[#b8860b]/30">
              <CardContent className="py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Your BETH Balance</p>
                    <p className="text-3xl font-bold text-white">{bethBalance.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">≈ ${bethBalance.toLocaleString()} USD</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={refreshBalances}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Transfer Tab Content */}
            {activeTab === 'transfer' && (
              <>
                <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Send className="w-5 h-5 text-[#b8860b]" />
                      Send BETH
                    </CardTitle>
                    <CardDescription>Transfer BETH tokens to any Ethereum address</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-2 block">Recipient Address</label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="0x..."
                          value={recipientAddress}
                          onChange={(e) => setRecipientAddress(e.target.value)}
                          className="bg-[#1a2d4a] border-[#2a3d5a] text-white font-mono flex-1"
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowSaveAddress(!showSaveAddress)}
                          className="border-[#1a2d4a]"
                        >
                          {showSaveAddress ? 'Cancel' : 'Save'}
                        </Button>
                      </div>
                      {recipientAddress && !validateAddress(recipientAddress) && (
                        <p className="text-xs text-red-400 mt-1">Invalid Ethereum address</p>
                      )}
                    </div>

                    {showSaveAddress && recipientAddress && validateAddress(recipientAddress) && (
                      <div className="p-3 bg-[#1a2d4a]/50 rounded-lg">
                        <label className="text-sm text-gray-400 mb-2 block">Label for this address</label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="e.g., My Exchange, Friend's Wallet"
                            value={newAddressLabel}
                            onChange={(e) => setNewAddressLabel(e.target.value)}
                            className="bg-[#1a2d4a] border-[#2a3d5a] text-white flex-1"
                          />
                          <Button onClick={saveAddress} className="bg-[#b8860b] hover:bg-[#9a7209]">
                            Save
                          </Button>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="text-sm text-gray-400 mb-2 block">Amount (BETH)</label>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                      />
                      <div className="flex justify-between mt-1">
                        <p className="text-xs text-gray-500">Available: {bethBalance.toLocaleString()} BETH</p>
                        <button 
                          className="text-xs text-[#b8860b] hover:underline"
                          onClick={() => setAmount(bethBalance.toString())}
                        >
                          Max
                        </button>
                      </div>
                    </div>

                    <Button 
                      className="w-full bg-[#b8860b] hover:bg-[#9a7209]"
                      onClick={handleTransfer}
                      disabled={sending || !recipientAddress || !amount}
                    >
                      {sending ? 'Sending...' : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send BETH
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                {/* Saved Addresses */}
                {savedAddresses.length > 0 && (
                  <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-blue-400" />
                        Saved Addresses
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {savedAddresses.map((addr, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-center justify-between p-3 bg-[#1a2d4a]/50 rounded-lg hover:bg-[#1a2d4a] cursor-pointer"
                            onClick={() => selectSavedAddress(addr.address)}
                          >
                            <div>
                              <p className="text-white font-medium">{addr.label}</p>
                              <p className="text-xs text-gray-500 font-mono">{addr.address}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(addr.address);
                                }}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-400"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeSavedAddress(addr.address);
                                }}
                              >
                                ×
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Auto Loan Payment Tab Content */}
            {activeTab === 'autoloan' && (
              <>
                {/* Payment Provider Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(PAYMENT_PROVIDERS).map(([key, provider]) => (
                    <Card 
                      key={key}
                      className={`cursor-pointer transition-all ${
                        selectedProvider === key
                          ? provider.color === 'blue' 
                            ? 'bg-blue-900/30 border-blue-500 ring-2 ring-blue-500/30'
                            : 'bg-green-900/30 border-green-500 ring-2 ring-green-500/30'
                          : 'bg-[#0d1d35] border-[#1a2d4a] hover:border-gray-500'
                      }`}
                      onClick={() => setSelectedProvider(key)}
                    >
                      <CardContent className="py-6">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                            provider.color === 'blue' ? 'bg-blue-900/50' : 'bg-green-900/50'
                          }`}>
                            {provider.logo}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-bold">{provider.name}</p>
                              {selectedProvider === key && (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              )}
                            </div>
                            <p className="text-gray-400 text-sm">{provider.description}</p>
                            <div className="mt-3 space-y-1">
                              {provider.features.map((feature, idx) => (
                                <p key={idx} className="text-xs text-gray-500 flex items-center gap-1">
                                  <Shield className="w-3 h-3" /> {feature}
                                </p>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Payment Form */}
                <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Car className="w-5 h-5 text-green-400" />
                      Pay Auto Loan with {PAYMENT_PROVIDERS[selectedProvider]?.name}
                    </CardTitle>
                    <CardDescription>
                      Use your BETH tokens to make auto loan payments
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-400 mb-2 block">Loan/Account ID (optional)</label>
                        <Input
                          placeholder="e.g., LOAN-123456"
                          value={loanId}
                          onChange={(e) => setLoanId(e.target.value)}
                          className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400 mb-2 block">Payment Amount (BETH)</label>
                        <Input
                          type="number"
                          placeholder="0.00"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                        />
                        <div className="flex justify-between mt-1">
                          <p className="text-xs text-gray-500">Available: {bethBalance.toLocaleString()} BETH</p>
                          <button 
                            className="text-xs text-green-400 hover:underline"
                            onClick={() => setPaymentAmount(bethBalance.toString())}
                          >
                            Max
                          </button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm text-gray-400 mb-2 block">Payment Note (optional)</label>
                      <Input
                        placeholder="e.g., Monthly payment for January"
                        value={paymentNote}
                        onChange={(e) => setPaymentNote(e.target.value)}
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                      />
                    </div>

                    <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                      <div className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-green-400 mt-0.5" />
                        <div className="text-sm">
                          <p className="text-white font-medium">How it works</p>
                          <ol className="text-gray-400 mt-1 space-y-1 list-decimal list-inside">
                            <li>Your BETH is sent to {PAYMENT_PROVIDERS[selectedProvider]?.name}</li>
                            <li>They convert it to USD at market rate</li>
                            <li>Payment is applied to your auto loan</li>
                            <li>You receive a confirmation receipt</li>
                          </ol>
                        </div>
                      </div>
                    </div>

                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={handleAutoLoanPayment}
                      disabled={sending || !paymentAmount}
                    >
                      {sending ? 'Processing...' : (
                        <>
                          <DollarSign className="w-4 h-4 mr-2" />
                          Pay {paymentAmount || '0'} BETH via {PAYMENT_PROVIDERS[selectedProvider]?.name}
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-gray-500 text-center">
                      By proceeding, you authorize {PAYMENT_PROVIDERS[selectedProvider]?.name} to process this payment.
                      Transaction fees may apply.
                    </p>
                  </CardContent>
                </Card>

                {/* Provider Info */}
                <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-3">
                        <Building className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-white font-medium">{PAYMENT_PROVIDERS[selectedProvider]?.name}</p>
                          <p className="text-xs text-gray-500">Licensed payment processor</p>
                        </div>
                      </div>
                      <a 
                        href={PAYMENT_PROVIDERS[selectedProvider]?.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="outline" size="sm">
                          Learn More <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Recent Transfers */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-purple-400" />
                  Recent Transactions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentTransfers.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No transactions yet</p>
                ) : (
                  <div className="space-y-2">
                    {recentTransfers.slice(0, 10).map((transfer, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-[#1a2d4a]/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <ArrowUpRight className="w-5 h-5 text-red-400" />
                          <div>
                            <p className="text-white text-sm">
                              Sent to {transfer.to?.slice(0, 8)}...{transfer.to?.slice(-6)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(transfer.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-medium">-{transfer.amount} BETH</p>
                          {transfer.tx_hash && (
                            <a 
                              href={`https://etherscan.io/tx/${transfer.tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-[#b8860b] hover:underline"
                            >
                              View TX
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Notice */}
        <div className="p-4 bg-[#0d1d35] rounded-lg border border-[#1a2d4a]">
          <p className="text-xs text-gray-500 text-center">
            <strong className="text-gray-400">Notice:</strong> All transactions are final and recorded on the Ethereum blockchain. 
            Network fees (gas) apply. For auto loan payments, ensure your loan account information is accurate.
          </p>
        </div>
      </main>
    </div>
  );
};

export default TransferBeth;
